import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { BUSINESS } from "@/components/site/constants";
import { canonical } from "@/components/site/site-config";
import {
  HERO_CREW,
  METAL_INSTALL,
  WHITE_METAL_AERIAL,
  BLUE_METAL_LECANTO,
  GALVALUME_INVERNESS,
  TAN_STANDING_SEAM_PINE_RIDGE,
  ResponsivePicture,
} from "@/components/site/images";
import {
  HERNANDO_GALVALUME_FINISHED,
  CRYSTAL_RIVER_BLUE_SHINGLE,
  HOMOSASSA_BLUE_METAL_IN_PROGRESS,
  HERNANDO_GALVALUME_AERIAL,
  LECANTO_BLACK_SHINGLE,
  FLORAL_CITY_BLACK_SHINGLE,
  LECANTO_METAL_BEFORE,
  LECANTO_METAL_AFTER,
  LECANTO_STANDING_SEAM_GALVALUME,
  BEVERLY_HILLS_SHINGLE_TEAR_OFF,
  BEVERLY_HILLS_SHINGLE_UNDERLAYMENT,
  BEVERLY_HILLS_SHINGLE_IN_PROGRESS,

} from "@/components/site/project-images";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { Link } from "@tanstack/react-router";
import { CASE_STUDIES } from "@/components/site/case-studies";

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      { title: "Roofing Project Gallery | Citrus County, FL | Destination Roofing" },
      { name: "description", content: "View completed roof replacement, repair, metal, shingle and flat-roofing projects by Destination Roofing throughout Citrus County, FL." },
      { property: "og:title", content: "Roofing Project Gallery | Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Real roofing project photos from Citrus County, FL." },
      { property: "og:url", content: canonical("/gallery") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: BLUE_METAL_LECANTO.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/gallery") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbListJsonLd([{ label: "Gallery", href: "/gallery" }])),
    }],
  }),
  component: Page,
});

const images = [
  {
    image: HERNANDO_GALVALUME_FINISHED,
    alt: "Completed Galvalume metal roof installed by Destination Roofing Inc. in Hernando, Florida",
    caption: "Galvalume metal roof — Hernando, FL",
  },
  {
    image: CRYSTAL_RIVER_BLUE_SHINGLE,
    alt: "Completed blue architectural shingle roof by Destination Roofing Inc. in Crystal River, Florida",
    caption: "Blue shingle roof — Crystal River, FL",
  },
  {
    image: HOMOSASSA_BLUE_METAL_IN_PROGRESS,
    alt: "Destination Roofing crew installing a blue metal roof in Homosassa, Florida",
    caption: "Blue metal roof install in progress — Homosassa, FL",
  },
  {
    image: HERNANDO_GALVALUME_AERIAL,
    alt: "Aerial view of a completed Galvalume metal roof by Destination Roofing Inc. in Hernando, Florida",
    caption: "Aerial — Galvalume metal roof, Hernando, FL",
  },
  {
    image: LECANTO_BLACK_SHINGLE,
    alt: "Completed black architectural shingle roof by Destination Roofing Inc. in Lecanto, Florida",
    caption: "Black shingle roof — Lecanto, FL",
  },
  {
    image: FLORAL_CITY_BLACK_SHINGLE,
    alt: "Completed black shingle roof on a new-construction home by Destination Roofing Inc. in Floral City, Florida",
    caption: "Black shingle roof — Floral City, FL",
  },
  {
    image: BLUE_METAL_LECANTO,
    alt: "Destination Roofing crew installing a blue metal roof in Lecanto, Florida",
    caption: "Blue metal-roof installation in Lecanto, FL",
  },
  {
    image: LECANTO_STANDING_SEAM_GALVALUME,
    alt: "26-gauge Galvalume standing seam metal roof installed by Destination Roofing in Lecanto, Florida",
    caption: "Standing seam Galvalume metal roof — Lecanto, FL",
  },
  {
    image: GALVALUME_INVERNESS,
    alt: "Galvalume metal roof replacement completed by Destination Roofing in Inverness, Florida",
    caption: "Galvalume metal-roof replacement in Inverness, FL",
  },
  {
    image: TAN_STANDING_SEAM_PINE_RIDGE,
    alt: "Tan standing-seam metal roof installed on a new-construction home in Pine Ridge, Florida",
    caption: "Standing-seam metal roof in Pine Ridge, FL",
  },
  {
    image: HERO_CREW,
    alt: "Destination Roofing crew with company truck on a Citrus County, FL job site",
    caption: "Our crew on-site in Citrus County",
  },
  {
    image: METAL_INSTALL,
    alt: "Metal roof installation in progress in Citrus County, FL",
    caption: "Metal roof install in progress",
  },
  {
    image: WHITE_METAL_AERIAL,
    alt: "Completed white metal roof installed by Destination Roofing in Crystal River, FL",
    caption: "Completed white metal roof — Crystal River",
  },
];

const BEVERLY_HILLS_STAGES = [
  {
    stage: "Tear-Off",
    image: BEVERLY_HILLS_SHINGLE_TEAR_OFF,
    alt: "Destination Roofing crew tearing off an existing shingle roof in Beverly Hills, Florida",
    caption: "Crew removing the existing shingle roofing from multiple roof sections.",
  },
  {
    stage: "Underlayment",
    image: BEVERLY_HILLS_SHINGLE_UNDERLAYMENT,
    alt: "Synthetic roof underlayment installed during a shingle replacement in Beverly Hills, Florida",
    caption: "Synthetic underlayment installed before the new shingles are applied.",
  },
  {
    stage: "Installation Preparation",
    image: BEVERLY_HILLS_SHINGLE_IN_PROGRESS,
    alt: "Residential shingle roof replacement in progress in Beverly Hills, Florida",
    caption: "Roofing materials staged while the Beverly Hills replacement project continues.",
  },
];


function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Gallery", href: "/gallery" }]} />
      <PageHero
        eyebrow="Gallery"
        title="Roofing Projects in Citrus County, FL"
        subtitle="Real project photos from Destination Roofing Inc. jobs across Citrus County, FL."
      />

      {/* Project hub */}
      <section className="mx-auto max-w-7xl px-4 pt-14">
        <div className="max-w-2xl">
          <div className="text-sm font-bold uppercase tracking-widest text-cta">Project Case Studies</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-display font-bold">Real Roofing Projects by Community</h2>
          <p className="mt-2 text-muted-foreground">Each project has its own page with photographs, the roofing system shown, and what we look for on that type of roof.</p>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CASE_STUDIES.map((s) => {
            const cover = (s.after ?? s.during ?? s.before ?? [])[0];
            return (
              <article key={s.slug} className="rounded-xl overflow-hidden border border-border bg-white flex flex-col">
                <div className="relative">
                  <ResponsivePicture image={cover.image} alt={cover.alt} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" imgClassName="w-full aspect-[4/3] object-cover" />
                  {s.statusState === "in-progress" && (
                    <span className="absolute top-3 left-3 rounded-full bg-cta text-cta-foreground text-xs font-bold px-3 py-1">In Progress</span>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="text-xs font-bold uppercase tracking-widest text-cta">{s.city}, FL · {s.roofType}</div>
                  <h3 className="mt-1 font-display text-lg font-bold">
                    <Link to={`/projects/${s.slug}` as string} className="hover:text-brand">{s.breadcrumbLabel}</Link>
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{s.summary}</p>
                  <Link to={`/projects/${s.slug}` as string} className="mt-auto pt-3 text-sm font-semibold text-brand hover:underline">View project →</Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Before & After */}
      <section className="mx-auto max-w-7xl px-4 pt-14">
        <div className="max-w-2xl">
          <div className="text-sm font-bold uppercase tracking-widest text-cta">Before &amp; After</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-display font-bold">Lecanto Metal Roof Replacement</h2>
          <p className="mt-2 text-muted-foreground">
            A rusted, failing metal roof in Lecanto, FL replaced with a new metal roofing system by Destination Roofing Inc.
          </p>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <figure className="rounded-xl overflow-hidden border border-border bg-white">
            <div className="relative">
              <ResponsivePicture
                image={LECANTO_METAL_BEFORE}
                alt="Rusted and damaged metal roof in Lecanto, Florida before replacement by Destination Roofing Inc."
                sizes="(min-width: 768px) 50vw, 100vw"
                imgClassName="w-full aspect-[4/3] object-cover"
              />
              <span className="absolute top-3 left-3 rounded-full bg-foreground text-background text-xs font-bold px-3 py-1">Before</span>
            </div>
            <figcaption className="p-3 text-sm text-muted-foreground">Before — failing, rusted metal roof in Lecanto, FL.</figcaption>
          </figure>
          <figure className="rounded-xl overflow-hidden border border-border bg-white">
            <div className="relative">
              <ResponsivePicture
                image={LECANTO_METAL_AFTER}
                alt="New metal roof installed in Lecanto, Florida by Destination Roofing Inc. after replacement"
                sizes="(min-width: 768px) 50vw, 100vw"
                imgClassName="w-full aspect-[4/3] object-cover"
              />
              <span className="absolute top-3 left-3 rounded-full bg-cta text-cta-foreground text-xs font-bold px-3 py-1">After</span>
            </div>
            <figcaption className="p-3 text-sm text-muted-foreground">After — new metal roof installed by Destination Roofing Inc. <Link to="/projects/lecanto-metal-roof-before-after" className="text-brand underline">Project details</Link></figcaption>
          </figure>
        </div>
      </section>

      {/* Beverly Hills shingle replacement — in progress */}
      <section className="mx-auto max-w-7xl px-4 pt-14">
        <div className="max-w-2xl">
          <div className="text-sm font-bold uppercase tracking-widest text-cta">Project In Progress</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-display font-bold">
            Beverly Hills Shingle Roof Replacement — In Progress
          </h2>
          <p className="mt-2 text-muted-foreground">
            A residential shingle-roof replacement currently underway in Beverly Hills, FL. These photos show the work
            in progress — the roof is not finished.{" "}
            <Link to="/projects/shingle-roof-replacement-beverly-hills-fl" className="text-brand underline">View the full project page</Link>.
          </p>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BEVERLY_HILLS_STAGES.map((s) => (
            <figure key={s.stage} className="rounded-xl overflow-hidden border border-border bg-white">
              <div className="relative">
                <ResponsivePicture
                  image={s.image}
                  alt={s.alt}
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                  imgClassName="w-full aspect-[16/9] object-cover"
                />
                <span className="absolute top-3 left-3 rounded-full bg-foreground text-background text-xs font-bold px-3 py-1">
                  {s.stage}
                </span>
              </div>
              <figcaption className="p-3 text-sm text-muted-foreground">{s.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>



      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {images.map((img, i) => (
            <figure key={i} className="rounded-xl overflow-hidden border border-border bg-white">
              <ResponsivePicture
                image={img.image}
                alt={img.alt}
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                imgClassName="w-full aspect-[4/3] object-cover"
              />
              <figcaption className="p-3 text-sm text-muted-foreground">{img.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}

